import { Linking } from 'react-native';
import mime from 'react-native-mime-types';
import {
  ALLOWED_MIME_TYPES,
  CLOUDINARY_IMAGE_MAX_FILE_SIZE,
  IS_WEB,
} from './constants/utility';
import { CloudinaryResourceType, SocialPlatform } from '@/shared/types';
import * as ExpoImagePicker from 'expo-image-picker';
import * as FileSystem from 'expo-file-system';
import useStore from '@/stores/useStore';
import SentryService from '@/services/sentry/Sentry.service';
import {
  Consultation,
  ConsultationStatus,
  GraphQlErrorCode,
  SessionType,
  User,
} from '@/shared/types/generated';
import { ApolloError } from '@apollo/client';
import { Timezone } from '@/shared/constants/availability';
import * as Localization from 'expo-localization';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import {
  CommonActions,
  NavigationProp,
  ParamListBase,
} from '@react-navigation/native';
import { ROUTES } from '@/shared/constants/routes';
import { RouteType } from '@/shared/types/navigation';
import React from 'react';
import { createRoot } from 'react-dom/client';
import ImageCropper from '@/shared/components/ImageCropper';

export const openImagePicker = async (
  aspectRatio: number = 1,
): Promise<string> => {
  if (IS_WEB) {
    // Web implementation using expo-image-picker with custom cropper
    const permissionResult =
      await ExpoImagePicker.requestMediaLibraryPermissionsAsync();

    if (permissionResult.granted === false) {
      alert('Permission to access camera roll is required!');
      return '';
    }

    const pickerResult = await ExpoImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      allowsEditing: false,
    });

    if (!pickerResult.canceled && pickerResult.assets?.length > 0) {
      const uri = pickerResult.assets[0].uri;
      if (uri.includes('image/heic')) {
        throw new Error(
          'Invalid file type, please use a JPEG or PNG. If you want to use HEIC, please use the mobile app.',
        );
      }

      const mimeType = pickerResult.assets[0].mimeType;
      if (mimeType && !ALLOWED_MIME_TYPES.image.includes(mimeType)) {
        throw new Error('Invalid file type, please use a JPEG or PNG.');
      }

      const response = await fetch(uri);
      const blob = await response.blob();
      const fileSize = blob.size;

      const maxFileSize = CLOUDINARY_IMAGE_MAX_FILE_SIZE;

      if (fileSize && fileSize > maxFileSize) {
        throw new Error('File is too large, must be under 10MB');
      }

      // Use the custom cropper for web
      return new Promise((resolve) => {
        const showCropper = () => {
          // Create a container for the cropper
          const container = document.createElement('div');
          container.id = 'image-cropper-container';
          document.body.appendChild(container);

          const handleCropComplete = (croppedUri: string) => {
            // Clean up the cropper
            if (container && container.parentNode) {
              container.parentNode.removeChild(container);
            }
            resolve(croppedUri);
          };

          const handleCancel = () => {
            // Clean up the cropper
            if (container && container.parentNode) {
              container.parentNode.removeChild(container);
            }
            resolve('');
          };

          // Create root and render the cropper
          const root = createRoot(container);
          root.render(
            React.createElement(ImageCropper, {
              imageUri: uri,
              onCropComplete: handleCropComplete,
              onCancel: handleCancel,
              aspectRatio: aspectRatio,
            }),
          );
        };

        showCropper();
      });
    }
    return '';
  } else {
    // Mobile implementation using conditional require

    // Conditional require only loads on mobile
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const ImagePicker = require('react-native-image-crop-picker').default;

    try {
      // Calculate width and height based on aspect ratio
      // Use a base size and scale appropriately for different aspect ratios
      const baseSize = 1000;
      let width: number, height: number;

      if (aspectRatio >= 1) {
        // Landscape or square: width is longer
        width = baseSize;
        height = Math.round(baseSize / aspectRatio);
      } else {
        // Portrait: height is longer
        height = baseSize;
        width = Math.round(baseSize * aspectRatio);
      }

      const image = await ImagePicker.openPicker({
        width: width,
        height: height,
        cropping: true,
        mediaType: 'photo',
      });

      if (image.mime && !ALLOWED_MIME_TYPES.image.includes(image.mime)) {
        throw new Error('Invalid file type');
      }

      // Validate file size
      const fileInfo = await FileSystem.getInfoAsync(image.path, {
        size: true,
      });
      // @ts-expect-error size is a conditional property contingent on this size: true
      const fileSize = fileInfo.size;
      const maxFileSize = CLOUDINARY_IMAGE_MAX_FILE_SIZE;

      if (fileSize && fileSize > maxFileSize) {
        throw new Error('File is too large, must be under 10MB');
      }

      return image.path;
    } catch (error) {
      // Handle user cancellation
      if (
        error &&
        typeof error === 'object' &&
        'code' in error &&
        error.code === 'E_PICKER_CANCELLED'
      ) {
        ImagePicker.close();
        return '';
      }
      throw error;
    }
  }
};

const getSocialMediaUrl = (handle: string, platform: SocialPlatform) => {
  if (!handle) return null;

  let formattedHandle = handle.trim();

  if (platform === 'instagram') {
    formattedHandle = formattedHandle.startsWith('@')
      ? formattedHandle.slice(1)
      : formattedHandle; // Remove @ if present
    return `https://www.instagram.com/${formattedHandle}`;
  }

  if (platform === 'tiktok') {
    formattedHandle = formattedHandle.startsWith('@')
      ? formattedHandle
      : `@${formattedHandle}`; // Ensure @ is present
    return `https://www.tiktok.com/${formattedHandle}`;
  }

  if (platform === 'substack') {
    formattedHandle = formattedHandle.startsWith('@')
      ? formattedHandle
      : `@${formattedHandle}`;
    return `https://substack.com/${formattedHandle}`;
  }

  return null;
};

export const openSocialLink = (handle: string, platform: SocialPlatform) => {
  const url = getSocialMediaUrl(handle, platform);

  if (!url) {
    useStore.getState().setSnackbarState({
      open: true,
      message: 'Invalid social media handle',
      severity: 'error',
    });
    return;
  }

  if (IS_WEB) {
    window.open(url, '_blank');
  } else {
    Linking.openURL(url);
  }
};

export function getMimeType(
  fileName: string,
  category: CloudinaryResourceType = 'image',
): string {
  const mimeType = mime.lookup(fileName);

  if (!mimeType) {
    throw new Error('Could not determine MIME type for the file.');
  }

  if (!ALLOWED_MIME_TYPES[category].includes(mimeType)) {
    throw new Error(
      `Unsupported ${category} file type. Supported types are: ${ALLOWED_MIME_TYPES[category].join(', ')}`,
    );
  }

  return mimeType;
}

export const arraysEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, index) => val === sortedB[index]);
};

export const handleUrlOpen = async (url: string) => {
  if (IS_WEB) {
    window.open(url, '_blank');
  } else {
    try {
      await Linking.openURL(url);
    } catch (error) {
      SentryService.captureError('Error opening URL', error);
    }
  }
};

export const shouldSurfaceGraphQLError = (error: ApolloError): boolean => {
  // Extract error code from GraphQL response
  const errorCode = error.graphQLErrors?.[0]?.extensions?.code as unknown;

  // Ensure it's a valid GraphQlErrorCode before returning true
  return Object.values(GraphQlErrorCode).includes(
    errorCode as GraphQlErrorCode,
  );
};

export const formatTotalDuration = (seconds: number | undefined) => {
  if (!seconds || seconds < 60) {
    return `${seconds ?? 0} seconds`;
  }
  return `${Math.ceil(seconds / 60)} minutes`;
};

export const formatCallTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export const applyToBeStylist = () => {
  Linking.openURL('https://form.typeform.com/to/goaU9Ayq');
};

export const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Math.ceil(amount / 100));
};

export const getRateByTime = (rateInCents: number, timeInMinutes: number) => {
  const hours = timeInMinutes / 60;
  const total = rateInCents * hours;
  return total;
};

export const getStylistRate = (consultation: Consultation) => {
  if (consultation.isInstant) {
    return consultation.stylist?.pricing?.hourlyRate;
  }
  // In-person sessions use customInPersonHourlyRate
  if (consultation.sessionType === SessionType.InPersonSession) {
    return (
      consultation.stylist?.customInPersonHourlyRate ??
      consultation.stylist?.pricing?.hourlyRate
    );
  }
  return (
    consultation.stylist?.customHourlyRate ??
    consultation.stylist?.pricing?.hourlyRate
  );
};

export const getIsConsultationCancelled = (status: ConsultationStatus) => {
  return [
    ConsultationStatus.Cancelled,
    ConsultationStatus.ClientNoShow,
    ConsultationStatus.StylistNoShow,
  ].includes(status);
};
export const getDropdownOptionLabel = (
  value: string,
  options: { label: string; value: string }[],
) => {
  const option = options.find((option) => option.value === value);
  return option ? option.label : 'Select an option';
};

export const formatNameWithLastInitial = (user?: User | null) => {
  if (!user) return 'Missing user name';
  return `${user.firstName} ${user.lastName.charAt(0)}.`;
};

export const getCurrentTimeZone = () => {
  try {
    // Note: using getCalendars() since Localization.timezone is deprecated
    const calendars = Localization.getCalendars();
    return (
      calendars?.[0]?.timeZone ??
      Intl.DateTimeFormat().resolvedOptions().timeZone
    );
  } catch (error) {
    SentryService.captureError('Error getting current timezone', error);
    return Timezone.Eastern;
  }
};

export const resetNavigationToRoute = (
  navigation: NavigationProp<ParamListBase>,
  route: string,
) => {
  /*
    This is a workaround to fix an issue where the tab bar would not show up.
    Our (main) layout is setup with a Drawer and a custom TabBar, but is navigated
    to from other top level routes like onboarding. When this happens, the specific
    route in (main) is loaded, not the root of the Drawer containing the TabBar.

    We could in the future re-work our top level route organization to likely fix this.
  */
  navigation.dispatch(
    CommonActions.reset({
      index: 0,
      routes: [
        {
          name: '(main)',
          state: {
            routes: [
              {
                name: route,
              },
            ],
          },
        },
      ],
    }),
  );
};

export const isMessagesChannelScreen = (
  pathname: string,
  channelUrl?: string,
): boolean => {
  const root = `${ROUTES.MESSAGES.path}/`;

  // When we're at the root messages route (/messages/), it's considered a chat screen
  // if there's a channelUrl query parameter present
  if (pathname === root) {
    return Boolean(channelUrl);
  }

  // Otherwise, any deeper path under /messages/ is a channel screen
  return pathname.startsWith(root);
};

export const isPathMatching = (
  routePath: RouteType['path'],
  pathname: string,
) => {
  if (!pathname || !routePath) return false;

  const path = typeof routePath === 'string' ? routePath : routePath.pathname;
  // Handle root path specially
  if (pathname === '/' && path === '/') return true;
  // Handles both exact matches and nested routes
  return pathname.startsWith(path);
};

/**
 * Fisher-Yates shuffle algorithm for proper randomization
 * Creates a new array with elements in random order
 * @param array - The array to shuffle
 * @returns A new shuffled array
 */
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export const toTitleCase = (text: string): string => {
  return text
    .split(' ')
    .map((word) => {
      // Split by hyphens and apostrophes while preserving them
      const parts = word.split(/(['-])/);
      return parts
        .map((part) => {
          // Skip the separators (hyphens and apostrophes)
          if (part === '-' || part === "'") return part;
          // Capitalize the first letter of each part
          return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
        })
        .join('');
    })
    .join(' ');
};

/**
 * Checks if the current date is within the event date range
 * @param startDate - Event start date in YYYY-MM-DD format
 * @param endDate - Event end date in YYYY-MM-DD format
 * @param timezone - The timezone to use for comparison (defaults to Eastern Time)
 * @returns true if current date is within the event window, false otherwise
 */
export const isWithinEventDateRange = (
  startDate: string,
  endDate: string,
  timezone?: string,
): boolean => {
  try {
    // Use provided timezone or fallback to Eastern Time (New York)
    const tz = timezone || Timezone.Eastern;

    // Get current date in the specified timezone
    const now = new Date();
    const currentDateInTz = toZonedTime(now, tz);

    // Create event dates in the specified timezone at start and end of day
    const eventStartDate = fromZonedTime(`${startDate} 00:00:00`, tz);
    const eventEndDate = fromZonedTime(`${endDate} 23:59:59`, tz);

    // Set current date to start of day in the same timezone for comparison
    const currentDateStartOfDay = fromZonedTime(
      `${currentDateInTz.getFullYear()}-${String(
        currentDateInTz.getMonth() + 1,
      ).padStart(2, '0')}-${String(currentDateInTz.getDate()).padStart(
        2,
        '0',
      )} 00:00:00`,
      tz,
    );

    return (
      currentDateStartOfDay >= eventStartDate &&
      currentDateStartOfDay <= eventEndDate
    );
  } catch (error) {
    SentryService.captureError('Error checking event date range', error);
    return false;
  }
};

/**
 * Extracts the display name from a Twilio participant identity.
 * Twilio identities are formatted as "userId-userName" (e.g., "8-Patrick")
 * This function returns just the userName part.
 *
 * @param identity - The full Twilio participant identity
 * @returns The display name (userName part), or the original identity if format is unexpected
 */
export function extractDisplayNameFromIdentity(identity: string): string {
  const hyphenIndex = identity.indexOf('-');
  if (hyphenIndex === -1) {
    // No hyphen found, return original identity
    return identity;
  }
  // Return everything after the first hyphen
  return identity.substring(hyphenIndex + 1);
}
